import React, { memo, useMemo, useCallback, MouseEvent } from "react";

import {
  Select,
  MenuItem,
  Typography,
  TableCell,
  IconButton,
  Tooltip,
  Box,
  Checkbox,
} from "@mui/material";

import CancelIcon from "@mui/icons-material/Cancel";

const sizeOptions = [
  { idSize: 1, size: "Pequeño" },
  { idSize: 2, size: "Promedio" },
  { idSize: 3, size: "Grande" },
  { idSize: 4, size: "Muy Grande" },
];

const complexityOptions = [
  { idComplexity: 1, complexity: "Baja" },
  { idComplexity: 2, complexity: "Promedio" },
  { idComplexity: 3, complexity: "Alta" },
];

const sizeColors: any = {
  1: "#D6F5E8",
  2: "#E8F1FF",
  3: "#FFF0CC",
  4: "#FFDADA",
};

const complexityColors: any = {
  1: "#D6F5E8",
  2: "#FFF0CC",
  3: "#FFDADA",
};

function APITaskTableRow({
  task,
  elementTypes,
  onRowDataChange,
  onDeleteRow,
  isActive,
  onActivate,
  isSelectedToDelete,
  onToggleDeleteSelection,
  isIgnored,
}: any) {
  const selectedType = useMemo(() => {
    return (
      elementTypes.find((x: any) => x.idElementType === task.idElementType)
        ?.name ?? "-"
    );
  }, [elementTypes, task.idElementType]);

  const selectedSize = useMemo(() => {
    return sizeOptions.find((x) => x.idSize === task.idSize)?.size ?? "-";
  }, [task.idSize]);

  const selectedComplexity = useMemo(() => {
    return (
      complexityOptions.find((x) => x.idComplexity === task.idComplexity)
        ?.complexity ?? "-"
    );
  }, [task.idComplexity]);

  const handleDelete = useCallback(() => {
    onDeleteRow(task.apiID);
  }, [task.apiID, onDeleteRow]);

  return (
    <>
      {/* CHECKBOX */}
      <TableCell
        align="center"
        padding="checkbox"
        sx={{
          width: isIgnored ? 0 : 48,
          minWidth: isIgnored ? 0 : 48,
          maxWidth: isIgnored ? 0 : 48,
          p: isIgnored ? 0 : undefined,
        }}
      >
        {!isIgnored && (
          <Checkbox
            checked={Boolean(isSelectedToDelete)}
            onClick={(e : any) => e.stopPropagation()}
            onChange={(e : any) => {
              e.stopPropagation();
              onToggleDeleteSelection();
            }}
          />
        )}
      </TableCell>
      {/* TITLE */}
      <TableCell
        onClick={isIgnored ? undefined : onActivate}
        sx={{
          cursor: isIgnored ? "default" : "pointer",
          verticalAlign: "top",
          whiteSpace: "normal",
          wordBreak: "break-word",
          opacity: isIgnored ? 0.65 : 1,
        }}
      >
        <Typography fontWeight={500}>{task.name}</Typography>
      </TableCell>
      {/* TYPE */}
      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
        {!isActive || isIgnored ? (
          <Typography fontSize={13}>{selectedType}</Typography>
        ) : (
          <Select
            size="small"
            value={task.idElementType}
            onChange={(e) =>
              onRowDataChange.onElementTypeChange(
                task.apiID,
                e.target.value as number,
              )
            }
          >
            {elementTypes.map((e: any) => (
              <MenuItem key={e.idElementType} value={e.idElementType}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </TableCell>

      {/* SIZE */}
      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.4,
            borderRadius: 1,
            backgroundColor: sizeColors[task.idSize],
            fontWeight: 600,
            fontSize: 12,
            display: "inline-block",
          }}
        >
          {selectedSize}
        </Box>
      </TableCell>

      {/* COMPLEXITY */}
      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.4,
            borderRadius: 1,
            backgroundColor: complexityColors[task.idComplexity],
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          {selectedComplexity}
        </Box>
      </TableCell>

      {/* EFFORT */}
      <TableCell align="center">
        <Typography fontWeight={600}>{task.estimatedTime}h</Typography>
      </TableCell>

      {/* DURATION */}
      <TableCell align="center">
        <Typography fontWeight={600}>{task.estimatedDays}d</Typography>
      </TableCell>

      {/* STATUS */}
      <TableCell align="center">
        <Typography fontSize={13}>{task.status}</Typography>
      </TableCell>

      {/* DELETE */}
      <TableCell align="center">
        {isActive && !isIgnored && (
          <Tooltip title="Quitar">
            <IconButton
              color="error"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </>
  );
}

export default memo(APITaskTableRow);
